import * as Preact from 'preact';
import * as H from 'preact/hooks';
import { AppControlContext } from './context/appControl';
import { constructModelUrl, useModelViewer } from './modelViewer/useModelViewer';
import * as Selectors from '../store/selectors';
import * as Core from '../store/core';
import * as VariantData from '../store/variantData';
import { useDispatch, useSelector } from './hooks/redux';
import { logError, logWarning } from '../store/actions/logging';
import * as Inspiration from '../store/actions/inspiration';
import * as Navigation from '../store/actions/navigation';
import * as AR from '@dora/arctic';
import * as Ios from './inspiration/iOS';

import { launchInspirationWithProductID } from '../data/url';
import {
    QrCodeDrawer,
    TopBar,
    StartupScreen,
    Fab,
    CheckoutButton,
    StyledSwitch,
    PictureModalWithButton,
    InspirationCardPanel,
    Sidebar,
    IconWithLabel,
} from '@dora/components';
import Box from '@mui/material/Box';
import { Platform, appBarHeight } from '@dora/components/src/sizing';
import * as RendererError from './rendererError';
import { useTheme, CircularProgress, Dialog } from '@mui/material';
import { ArIcon, BrushIcon, SizeIcon } from '@dora/components/src/icons';
import { Cart, CheckoutLocation } from './hooks/useParentAPI';
import { useFeatureFlag } from './hooks/useFeatureFlag';
import { IS_IOS } from '@google/model-viewer/lib/constants';
import { useLoadVariantData } from './inspiration/useGetVariantData';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/Option';
import { ProductAttributes } from '@dora/backpack';
import {
    findNewVariant,
    isAttributeCompatible,
    isAttributeSelected,
    sortVariantAttributes,
} from '../data/productVariant';

export const Component = (props: {
    platform: Platform;
    proceedToCart: undefined | ((cart: Cart, from: CheckoutLocation) => void);
}) => {
    const inspirationData = useSelector(Selectors.Core.inspirationData);
    const modelBaseUrl = useSelector(Selectors.Core.arUrl);
    const conversionId = useSelector(Selectors.ConversionId.conversionId);
    const arCompatibility = useSelector(Selectors.Core.arCompatibility);
    const useCustomIOSArJourney = useFeatureFlag('custom-iOS-ar-journey');
    const viewDifferentVariantsEnabled = useFeatureFlag('vi-view-different-variants');
    const variantOptions = useSelector(Selectors.VariantData.variantOptions);
    const variantAttributes = useSelector(Selectors.VariantData.variantAttributes);

    useLoadVariantData(viewDifferentVariantsEnabled);

    const iOSArElement = H.useMemo(() => Ios.bootstrap(), []);

    const [currentVariant, setCurrentVariant] = H.useState(inspirationData?.launchData.productId);
    const [viewInArModalOpen, setViewInArModalOpen] = H.useState(
        (inspirationData?.launchData.shouldOpenArModal &&
            arCompatibility === AR.Compatibility.Compatible) ??
            false,
    );

    const modelUrl =
        modelBaseUrl && inspirationData && currentVariant
            ? constructModelUrl(modelBaseUrl, currentVariant, inspirationData.launchData.token)
            : undefined;

    const modelViewerTargetElementId = 'model-viewer-target';
    const appControl = H.useContext(AppControlContext);
    const arModalText = useSelector(Selectors.Core.arModalText);
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);
    const currentUrl = useSelector(Selectors.Core.currentUrl);
    const dispatch = useDispatch();

    const theme = useTheme();
    const [dimensionsVisible, setdimensionsVisible] = H.useState(
        inspirationData?.launchData.dimensionsVisibleInViewer ?? true,
    );

    const modelViewer = useModelViewer(
        true,
        dimensionsVisible,
        theme,
        modelUrl,
        inspirationData?.launchData.placement,
        modelViewerTargetElementId,
        () => dispatch(Inspiration.user3dSceneInteractionAction()),
    );

    const updateVariant = (attributeType: ProductAttributes.AttributeType, attributeId: string) => {
        pipe(
            O.Do,
            O.bind('variants', () => O.fromNullable(variantOptions)),
            O.bind('currentVariantAttributes', ({ variants }) =>
                O.fromNullable(variants.find(opt => opt.productId === currentVariant)?.attributes),
            ),
            O.bind('newVariant', ({ variants, currentVariantAttributes }) => {
                return O.fromNullable(
                    findNewVariant(variants, attributeType, attributeId, currentVariantAttributes),
                );
            }),
            O.fold(
                // TODO: handle this case properly
                () => {
                    dispatch(logWarning('could not update variant'));
                },
                ({ newVariant }) => {
                    if (currentVariant !== newVariant.productId) {
                        // Manually override the current dimensionsVisible setting until load is complete
                        modelViewer.setDimensionsEnabled(false);
                        setCurrentVariant(newVariant.productId);
                    }
                },
            ),
        );
    };

    const [qrDrawerOpen, setQrDrawerOpen] = H.useState(false);

    H.useEffect(() => {
        appControl?.enhanceCloseApp('inspiration-runway', () => {
            dispatch(Core.clearViewData());
            dispatch(VariantData.clearVariantData());
        });
    });

    // in the case of an error, display an empty component
    if (arModalText == undefined) {
        dispatch(logError('Displaying Inspiration Runway while not in Inspiration Mode'));
        return null;
    } else if (inspirationData === undefined) {
        dispatch(logError('Displaying Inspiration Runway without a set product ID'));
        return null;
    }

    const generateQrUrl = () =>
        launchInspirationWithProductID(
            // We need the iFrame parent's URL
            currentUrl,
            currentVariant ?? inspirationData.launchData.productId,
        ).toString();

    const checkout = () => {
        // This method will only be called if all of these have been set, but typescript doesn't know that
        if (props.proceedToCart && conversionId && currentVariant) {
            props.proceedToCart(
                {
                    conversionId,
                    products: [
                        {
                            id: currentVariant,
                            quantity: 1,
                        },
                    ],
                },
                'model viewer',
            );
            appControl?.closeApp();
        }
    };

    const onArButtonClick = () => {
        if (modelViewer.canActivateAr) {
            dispatch(Navigation.viewInArAction({ mode: 'simple' }));

            // special case for iOS - use model viewer to generate a USDZ then use a direct integration journey which includes add to cart button
            if (IS_IOS && useCustomIOSArJourney) {
                modelViewer.generateUSDZ().then(objectUrl => {
                    const modelUrl = new URL(objectUrl, self.location.toString());
                    const shouldShowARButton = props.proceedToCart !== undefined;
                    Ios.loadAR(modelUrl.toString(), checkout, shouldShowARButton, iOSArElement);
                    URL.revokeObjectURL(objectUrl);
                });
            } else {
                modelViewer.activate();
            }
        } else {
            dispatch(
                Inspiration.openArDrawerAction({
                    item_id: currentVariant ?? inspirationData.launchData.productId,
                }),
            );
            setQrDrawerOpen(true);
        }
    };

    const [activeLabel, setActiveLabel] = H.useState<ProductAttributes.AttributeType | undefined>(
        undefined,
    );

    const onLabelClick = (attributeType: ProductAttributes.AttributeType) => {
        let openPanel;
        if (activeLabel !== attributeType) {
            openPanel = true;
            setActiveLabel(attributeType);
        } else {
            setActiveLabel(undefined);
            openPanel = false;
        }
        dispatch(Inspiration.selectAttributeAction({ attributeType, openPanel }));
    };

    // We need this because the panel contents appear after the panel has fully transitioned
    const panelTransitionTime = 200;
    // Hide the AR button on unsupported mobile devices
    const showArButton =
        modelViewer.canActivateAr || (props.platform !== 'mobile' && !modelViewer.canActivateAr);

    // Show initial loading screen on first load while data is fetching
    const showStartupScreen = variantOptions === null || modelViewer.isInitialLoad;

    // Only show options sidebar when there is another product variant that can be swapped to
    const showOptionsSidebar = variantOptions && variantOptions.length > 1;

    return (
        <>
            {showStartupScreen && (
                <StartupScreen.Component
                    onClose={() => appControl?.closeApp()}
                    statusText={{ initial: '', middle: '', final: '' }}
                    logoUrl={
                        tenantIdentifier === 'moda'
                            ? 'assets/moda/logo.svg'
                            : 'assets/fixtuur-logo.svg'
                    }
                    sx={{ zIndex: theme.zIndex.snackbar + 1 }} // Needs to show above the Ar FAB (snackbar)
                />
            )}
            <Dialog open={viewInArModalOpen} sx={{ zIndex: theme.zIndex.snackbar }}>
                <PictureModalWithButton.Component
                    buttonText={arModalText.buttonText}
                    image="assets/inspiration-runway.png"
                    onButtonClick={onArButtonClick}
                    error={modelViewer.error ? modelViewer.error : undefined}
                    onCloseClick={() => setViewInArModalOpen(false)}
                    modalText={
                        arCompatibility === AR.Compatibility.Compatible
                            ? arModalText.buttonExplainerText
                            : arModalText.incompatibleDeviceDialogText.header
                    }
                    disabled={arCompatibility !== AR.Compatibility.Compatible}
                    loading={modelViewer.loading}
                />
            </Dialog>
            <Box
                height="100%"
                width="100%"
                sx={{
                    display: 'grid',
                    // Row 0 is for the app bar
                    // Row 1 is for the model viewer
                    gridTemplateRows: `${appBarHeight}px 1fr`,
                }}
            >
                <TopBar.Component onCloseClick={() => appControl?.closeApp()}>
                    {props.proceedToCart && (
                        <CheckoutButton.Component
                            total={arModalText.addToCartButton}
                            onBasketClick={checkout}
                            disabled={false}
                        />
                    )}
                </TopBar.Component>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '100%',
                            md: showOptionsSidebar
                                ? activeLabel
                                    ? '1fr 461px 89px'
                                    : '1fr 0 89px'
                                : '100% 0 0',
                        },
                        gridTemplateRows: {
                            xs: showOptionsSidebar
                                ? activeLabel
                                    ? '1fr 267px 0'
                                    : '1fr 0 78px'
                                : '100% 0',
                            md: '100%',
                        },
                        overflow: 'hidden',
                        transition: `all ${panelTransitionTime}ms ease`,
                        backgroundColor: '#EEF2FF',
                    }}
                >
                    <Box
                        id={modelViewerTargetElementId}
                        data-testid="modelViewerTargetElementId"
                        width="100%"
                        height="100%"
                        bgcolor="#EEF2FF"
                        position="relative"
                    >
                        {modelViewer.loading && !modelViewer.isInitialLoad && (
                            <Box sx={{ position: 'absolute', top: '20px', right: '20px' }}>
                                <CircularProgress color="secondary" />
                            </Box>
                        )}
                        {showArButton && (
                            <Fab.Component
                                variant="extended"
                                color="secondary"
                                onClick={onArButtonClick}
                                disabled={modelViewer.loading || modelViewer.error !== null}
                                sx={{
                                    position: 'absolute',
                                    bottom: '25px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    lineHeight: '1.25',
                                    zIndex: theme.zIndex.snackbar, // needs to show above renderer error (modal)
                                    display: 'flex',
                                    gap: '10px',
                                    width: 'max-content',
                                    '&:disabled': {
                                        backgroundColor: '#D7D7D8',
                                        color: '#A0A0A2',
                                    },
                                }}
                            >
                                <ArIcon />
                                View in your space
                            </Fab.Component>
                        )}
                        <StyledSwitch.Component
                            sx={{
                                position: 'absolute',
                                top: '20px',
                                left: '20px',
                                zIndex: 1,
                            }}
                            disabled={modelViewer.loading}
                            checked={dimensionsVisible}
                            multistate={true}
                            icons={{
                                on: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                                    '#272838',
                                )}" d="M16.5364 1.28045C16.8377 1.18256 17.1622 1.18256 17.4635 1.28045C17.6961 1.35603 17.8647 1.48158 17.9758 1.57587C18.0716 1.65725 18.1722 1.75789 18.2564 1.84215C18.2619 1.84767 18.2673 1.85313 18.2727 1.8585L22.1577 5.7435C22.242 5.82769 22.3426 5.92824 22.424 6.02409C22.5183 6.13517 22.6438 6.30377 22.7194 6.5364C22.8173 6.83766 22.8173 7.16218 22.7194 7.46345C22.6438 7.69609 22.5183 7.86469 22.424 7.97576C22.3426 8.0716 22.242 8.17213 22.1578 8.25632L8.25636 22.1577C8.17217 22.242 8.07161 22.3426 7.97576 22.424C7.86468 22.5183 7.69608 22.6438 7.46345 22.7194C7.16218 22.8173 6.83766 22.8173 6.5364 22.7194C6.30376 22.6438 6.13516 22.5183 6.02409 22.424C5.92825 22.3426 5.82772 22.242 5.74353 22.1578L1.84213 18.2563C1.75787 18.1722 1.65724 18.0716 1.57587 17.9758C1.48158 17.8647 1.35603 17.6961 1.28045 17.4635C1.18256 17.1622 1.18256 16.8377 1.28045 16.5364C1.35603 16.3038 1.48158 16.1352 1.57587 16.0241C1.65725 15.9282 1.75789 15.8277 1.84215 15.7435C1.84767 15.738 1.85313 15.7325 1.8585 15.7271L15.7271 1.8585C15.7325 1.85312 15.738 1.84766 15.7435 1.84213C15.8277 1.75787 15.9282 1.65724 16.0241 1.57587C16.1352 1.48158 16.3038 1.35603 16.5364 1.28045ZM16.9999 3.41414L15.4142 4.99989L16.7071 6.29278C17.0976 6.68331 17.0976 7.31647 16.7071 7.707C16.3165 8.09752 15.6834 8.09752 15.2929 7.707L14 6.4141L12.4142 7.99989L13.7071 9.29278C14.0976 9.68331 14.0976 10.3165 13.7071 10.707C13.3165 11.0975 12.6834 11.0975 12.2929 10.707L11 9.4141L9.41418 10.9999L10.7071 12.2928C11.0976 12.6833 11.0976 13.3165 10.7071 13.707C10.3165 14.0975 9.68338 14.0975 9.29286 13.707L7.99996 12.4141L6.41418 13.9999L7.70707 15.2928C8.0976 15.6833 8.0976 16.3165 7.70707 16.707C7.31655 17.0975 6.68338 17.0975 6.29286 16.707L4.99996 15.4141L3.41414 16.9999L6.99993 20.5857L20.5857 6.99992L16.9999 3.41414Z"/></svg>')`,
                                off: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
                                    '#272838',
                                )}" d="M16.5364 1.28045C16.8377 1.18256 17.1622 1.18256 17.4635 1.28045C17.6961 1.35603 17.8647 1.48158 17.9758 1.57587C18.0716 1.65725 18.1722 1.75789 18.2564 1.84215C18.2619 1.84767 18.2673 1.85313 18.2727 1.8585L22.1577 5.7435C22.242 5.82769 22.3426 5.92824 22.424 6.02409C22.5183 6.13517 22.6438 6.30377 22.7194 6.5364C22.8173 6.83766 22.8173 7.16218 22.7194 7.46345C22.6438 7.69609 22.5183 7.86469 22.424 7.97576C22.3426 8.0716 22.242 8.17213 22.1578 8.25632L8.25636 22.1577C8.17217 22.242 8.07161 22.3426 7.97576 22.424C7.86468 22.5183 7.69608 22.6438 7.46345 22.7194C7.16218 22.8173 6.83766 22.8173 6.5364 22.7194C6.30376 22.6438 6.13516 22.5183 6.02409 22.424C5.92825 22.3426 5.82772 22.242 5.74353 22.1578L1.84213 18.2563C1.75787 18.1722 1.65724 18.0716 1.57587 17.9758C1.48158 17.8647 1.35603 17.6961 1.28045 17.4635C1.18256 17.1622 1.18256 16.8377 1.28045 16.5364C1.35603 16.3038 1.48158 16.1352 1.57587 16.0241C1.65725 15.9282 1.75789 15.8277 1.84215 15.7435C1.84767 15.738 1.85313 15.7325 1.8585 15.7271L15.7271 1.8585C15.7325 1.85312 15.738 1.84766 15.7435 1.84213C15.8277 1.75787 15.9282 1.65724 16.0241 1.57587C16.1352 1.48158 16.3038 1.35603 16.5364 1.28045ZM16.9999 3.41414L15.4142 4.99989L16.7071 6.29278C17.0976 6.68331 17.0976 7.31647 16.7071 7.707C16.3165 8.09752 15.6834 8.09752 15.2929 7.707L14 6.4141L12.4142 7.99989L13.7071 9.29278C14.0976 9.68331 14.0976 10.3165 13.7071 10.707C13.3165 11.0975 12.6834 11.0975 12.2929 10.707L11 9.4141L9.41418 10.9999L10.7071 12.2928C11.0976 12.6833 11.0976 13.3165 10.7071 13.707C10.3165 14.0975 9.68338 14.0975 9.29286 13.707L7.99996 12.4141L6.41418 13.9999L7.70707 15.2928C8.0976 15.6833 8.0976 16.3165 7.70707 16.707C7.31655 17.0975 6.68338 17.0975 6.29286 16.707L4.99996 15.4141L3.41414 16.9999L6.99993 20.5857L20.5857 6.99992L16.9999 3.41414Z"/></svg>')`,
                            }}
                            onChange={() => {
                                dispatch(
                                    Inspiration.inspirationDimensionsToggledAction({
                                        isVisible: !dimensionsVisible,
                                    }),
                                );
                                setdimensionsVisible(!dimensionsVisible);
                            }}
                        />

                        {modelViewer.error && (
                            <RendererError.Component
                                heading={''} // TDC-1163 Low text design
                                paragraph={''} // TDC-1163 Low text design
                            />
                        )}
                    </Box>

                    <Box>
                        {variantAttributes && activeLabel && (
                            <InspirationCardPanel.Component
                                title={variantAttributes[activeLabel]!.displayName}
                                onClose={() => {
                                    setActiveLabel(undefined);
                                }}
                                attributes={sortVariantAttributes(
                                    variantAttributes[activeLabel]?.properties ?? [],
                                )}
                                onSelectOption={(
                                    attributeType,
                                    attributeId,
                                    optionValue,
                                    compatible,
                                ) => {
                                    updateVariant(attributeType, attributeId);
                                    dispatch(
                                        Inspiration.selectOptionAction({
                                            optionValue,
                                            compatible,
                                            currentProductId: currentVariant,
                                        }),
                                    );
                                }}
                                isAttributeSelected={(attributeType, attributeId) =>
                                    isAttributeSelected(
                                        variantOptions,
                                        attributeType,
                                        attributeId,
                                        currentVariant,
                                    )
                                }
                                isAttributeCompatible={(attributeType, attributeId) =>
                                    isAttributeCompatible(
                                        attributeType,
                                        attributeId,
                                        variantOptions ?? [],
                                        currentVariant,
                                    )
                                }
                                transitionTime={panelTransitionTime}
                            />
                        )}
                    </Box>

                    <Sidebar.Component>
                        {variantAttributes &&
                            (
                                Object.keys(
                                    variantAttributes,
                                ) as Array<ProductAttributes.AttributeType>
                            ).map(attributeType => (
                                <IconWithLabel.Component
                                    label={variantAttributes[attributeType]!.displayName}
                                    Icon={attributeType === 'dimension' ? SizeIcon : BrushIcon}
                                    onLabelClick={() => onLabelClick(attributeType)}
                                    active={activeLabel === attributeType}
                                />
                            ))}
                    </Sidebar.Component>
                </Box>
            </Box>
            <QrCodeDrawer.Component
                url={generateQrUrl()}
                open={qrDrawerOpen}
                platform={Platform.DESKTOP}
                portrait={true}
                heading={arModalText.viewInAr}
                body={arModalText.qrCodeDialogText.body}
                onClose={() => {
                    dispatch(
                        Inspiration.closeArDrawerAction({
                            item_id: currentVariant ?? inspirationData.launchData.productId,
                        }),
                    );
                    setQrDrawerOpen(false);
                }}
                title={arModalText.viewInAr}
                qrHintText={arModalText.qrCtaText}
                imageSrc="assets/inspiration-runway-desktop.png"
                sx={{ zIndex: theme.zIndex.snackbar }} // needs to show above RendererError (modal)
            />
        </>
    );
};
